import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import * as Styles from './index.styles';

const {
    StyledTabs,
    StyledTabTitles,
    StyledTabContent,
    StyledResponsiveTabTitles,
    StyledIcon,
} = Styles;

export const Tabs = ({
    defaultSelected,
    onSelect: onSelectProp,
    responsiveBreakpoint,
    children,
}) => {
    const ref = useRef();
    const [selected, setSelected] = useState(defaultSelected);
    const [isMobile, setIsMobile] = useState(false);
    const [width, setWidth] = React.useState(window.innerWidth);

    const childrenArray = React.Children.toArray(children);
    const childrenIdArray = childrenArray.map(child => child.props.id);
    const selectedChild = childrenArray.find(child => child.props.id === selected);

    const onSelect = id => {
        try {
            ref.current.scrollLeft = 0;
        } catch (err) {
            window.console.warn(err.message);
        }
        setSelected(id);
        onSelectProp(id);
    };

    const getCurrent = () => childrenIdArray.findIndex(id => id === selected);
    const getFirst = () => 0;
    const getLast = () => childrenIdArray.length - 1;

    const isFirst = (id = getCurrent()) => id === getFirst();
    const isLast = (id = getCurrent()) => id === getLast();

    const getPreviousTab = () => {
        const currentId = getCurrent();
        const previous = isFirst(currentId) ? getLast() : currentId - 1;
        return childrenIdArray[previous];
    };

    const getNextTab = () => {
        const currentId = getCurrent();
        const next = isLast(currentId) ? getFirst() : currentId + 1;
        return childrenIdArray[next];
    };

    const handlePrevious = () => {
        setSelected(getPreviousTab());
        onSelectProp(getPreviousTab());
    };

    const handleNext = () => {
        setSelected(getNextTab());
        onSelectProp(getNextTab());
    };

    const handleMobileClick = id => {
        if (id === 0) {
            handlePrevious();
        }
        if (id === 2) {
            handleNext();
        }
    };

    const handleResize = () => setWidth(window.innerWidth);

    useEffect(() => {
        if (!selectedChild) {
            setSelected(childrenArray[0].props.id);
        }
    }, []);

    useEffect(() => {
        setIsMobile(width < responsiveBreakpoint);
    }, [width]);

    useEffect(() => {
        window.addEventListener('resize', handleResize, false);
        return () => window.removeEventListener('resize', handleResize, false);
    }, []);

    const renderResponsiveTitles = () => {
        const selectedElement = childrenArray.filter(child => child.props.id === selected)[0];
        const elements = [selectedElement];
        elements.unshift(isFirst() ? childrenArray[getLast()] : childrenArray[getCurrent() - 1]);
        elements.push(isLast() ? childrenArray[getFirst()] : childrenArray[getCurrent() + 1]);
        return elements.map((child, i) =>
            React.cloneElement(child, {
                key: child.props.id,
                selected: child.props.id === selected,
                onSelect: () => handleMobileClick(i),
            }),
        );
    };

    return (
        <StyledTabs>
            {isMobile ? (
                <StyledResponsiveTabTitles>
                    <StyledIcon icon={['far', 'chevron-left']} size="2x" onClick={handlePrevious} />
                    {renderResponsiveTitles()}
                    <StyledIcon icon={['far', 'chevron-right']} size="2x" onClick={handleNext} />
                </StyledResponsiveTabTitles>
            ) : (
                <StyledTabTitles isMobile={isMobile}>
                    {childrenArray.map(child =>
                        React.cloneElement(child, {
                            key: child.props.id,
                            selected: child.props.id === selected,
                            onSelect,
                        }),
                    )}
                </StyledTabTitles>
            )}
            <StyledTabContent ref={ref}>
                {selectedChild ? selectedChild.props.children : null}
            </StyledTabContent>
        </StyledTabs>
    );
};

Tabs.propTypes = {
    onSelect: PropTypes.func,
    defaultSelected: PropTypes.string,
    responsiveBreakpoint: PropTypes.number,
    children: PropTypes.node.isRequired,
};

Tabs.defaultProps = {
    onSelect: () => null,
    defaultSelected: undefined,
    responsiveBreakpoint: 0,
};

Tabs.Styles = Styles;

export * from './Tab';
